import { apiClient, ApiError } from './client';
import type { components } from './schema';
export type Lead = components['schemas']['LeadView'];
export type LeadInput = components['schemas']['LeadIntakeInput'];
export type LeadContact = components['schemas']['LeadContact'];
export type LeadConfig = components['schemas']['LeadConfiguration'];
export type LeadKind = components['schemas']['LeadDraftInput']['kind'];
/**
 * What a visitor is told after sending the public form.
 *
 * `contactBy` is the time by which the workspace has promised to make contact. The server does not
 * send it yet; when it does, the receipt says it, and until then the receipt says only that a
 * person will be in touch, rather than guessing a time.
 */
export type PublicLeadReceipt = components['schemas']['PublicLeadReceipt'] & {
  readonly contactBy?: string;
};
function result<T>(r: { data?: T; response: Response; error?: unknown }): T {
  if (!r.response.ok || r.data === undefined)
    throw new ApiError(
      r.response.status,
      typeof r.error === 'object' && r.error !== null && 'detail' in r.error
        ? String(r.error.detail)
        : 'Intake request failed',
    );
  return r.data;
}
export async function listLeads(page = 0) {
  return result(await apiClient.GET('/api/intake', { params: { query: { page } } }));
}
export async function readLead(id: string) {
  return result(await apiClient.GET('/api/intake/{id}', { params: { path: { id } } }));
}
/** How many pages of the newest leads are read when looking for the lead behind a deal. */
const LEAD_LOOKUP_PAGES = 4;
/**
 * The lead a deal began as, or null when it did not begin as one or it is not among the newest.
 *
 * The server has no lookup by deal yet, so this reads the lead list a page at a time. Keeping it
 * to one function means a lookup by deal becomes a one-line change here.
 */
export async function leadForDeal(opportunityId: string): Promise<Lead | null> {
  for (let page = 0; page < LEAD_LOOKUP_PAGES; page += 1) {
    const leads = await listLeads(page);
    const found = leads.find((lead) => lead.opportunityId === opportunityId);
    if (found !== undefined) return found;
    if (leads.length === 0) return null;
  }
  return null;
}
export async function leadProposals(id: string, page = 0) {
  return result(
    await apiClient.GET('/api/intake/{id}/proposals', {
      params: { path: { id }, query: { page } },
    }),
  );
}
export async function createLead(body: LeadInput) {
  return result(await apiClient.POST('/api/intake', { body }));
}
export async function leadConfig() {
  return result(await apiClient.GET('/api/intake/configuration'));
}
export async function saveLeadConfig(body: LeadConfig) {
  return result(await apiClient.PUT('/api/intake/configuration', { body }));
}
export async function leadDraft(id: string, kind: LeadKind) {
  return result(
    await apiClient.POST('/api/intake/{id}/drafts', { params: { path: { id } }, body: { kind } }),
  );
}
/** Moves a lead along its way; closing a lead is `closeLead`, because that needs a reason. */
export async function disposition(lead: Lead, value: Exclude<Lead['disposition'], 'CLOSED'>) {
  return result(
    await apiClient.POST('/api/intake/{id}/disposition', {
      params: { path: { id: lead.id } },
      body: { expectedVersion: lead.version, disposition: value },
    }),
  );
}
/** How a closed lead's deal ends. */
export type LeadCloseOutcome = NonNullable<
  components['schemas']['LeadDispositionInput']['outcome']
>;
/** Closes a lead and ends its deal; the server refuses a close that has no reason. */
export async function closeLead(
  lead: Lead,
  close: { readonly reason: string; readonly outcome: LeadCloseOutcome },
) {
  return result(
    await apiClient.POST('/api/intake/{id}/disposition', {
      params: { path: { id: lead.id } },
      body: { expectedVersion: lead.version, disposition: 'CLOSED', ...close },
    }),
  );
}
export async function publicLeadForm(slug: string) {
  const response = await fetch('/api/public/intake/' + encodeURIComponent(slug));
  if (!response.ok) throw new Error('This intake form is unavailable.');
  return (await response.json()) as components['schemas']['PublicLeadForm'];
}
export async function submitPublicLead(
  slug: string,
  contact: LeadContact,
  website = '',
  captchaToken = '',
) {
  const response = await fetch('/api/public/intake/' + encodeURIComponent(slug), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contact, website, captchaToken }),
  });
  if (!response.ok) throw new Error('Submission failed. Review the form or try again later.');
  return (await response.json()) as PublicLeadReceipt;
}
